// Aliases: the name a user picks mapped to the preset that actually runs.
// Repointing one is a live change to what every future task runs, so the
// new value is typed rather than selected -- there is no list endpoint for
// presets, and a dropdown would have to be invented.
import { useEffect, useState } from "react";
import { adminListAliases, adminSetAlias, type Alias } from "../../api/client";

export default function AdminAliases() {
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const [newPreset, setNewPreset] = useState("");

  function load() {
    adminListAliases()
      .then(setAliases)
      .catch((e) => setError(e instanceof Error ? e.message : "failed to load aliases"));
  }
  useEffect(load, []);

  async function onSave(name: string) {
    const preset = edits[name];
    if (!preset) return;
    setError(null);
    try {
      await adminSetAlias(name, preset);
      setEdits((e) => ({ ...e, [name]: "" }));
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to update alias");
    }
  }

  async function onCreate() {
    if (!newName || !newPreset) return;
    setError(null);
    try {
      await adminSetAlias(newName, newPreset);
      setNewName("");
      setNewPreset("");
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to create alias");
    }
  }

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Aliases</h1>
          <p className="a-lede">
            An alias is what a user types; the preset is the definition that runs. Repointing an
            alias changes what every new task on it runs, immediately and without a deploy.
          </p>
        </div>
        <span className="a-panel__meta">{aliases.length} aliases</span>
      </div>

      {error && <p className="a-error">{error}</p>}

      <section className="a-panel">
        <div className="a-panel__head">
          <h2 className="a-panel__title">Mappings</h2>
          <span className="a-panel__meta">type a preset to repoint · blank to leave alone</span>
        </div>
        <div className="a-scroll">
          <table className="a-table">
            <thead>
              <tr>
                <th>Alias</th>
                <th>Preset</th>
                <th>UI template</th>
                <th>Repoint to</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {aliases.map((a) => (
                <tr key={a.name}>
                  <td className="a-table__hi">{a.name}</td>
                  <td className="a-table__dim">{a.preset}</td>
                  <td className="a-table__dim">{a.ui_template ?? "—"}</td>
                  <td>
                    <input
                      className="a-input"
                      placeholder={a.preset}
                      value={edits[a.name] ?? ""}
                      onChange={(e) => setEdits((ed) => ({ ...ed, [a.name]: e.target.value }))}
                    />
                  </td>
                  <td>
                    <button
                      className="a-btn"
                      disabled={!edits[a.name]}
                      onClick={() => void onSave(a.name)}
                    >
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="a-panel">
        <div className="a-panel__head">
          <h2 className="a-panel__title">New alias</h2>
          <span className="a-panel__meta">user-facing name → preset</span>
        </div>
        <div className="a-form">
          <label className="a-field">
            Alias name
            <input className="a-input" value={newName} onChange={(e) => setNewName(e.target.value)} />
          </label>
          <label className="a-field">
            Preset
            <input className="a-input" value={newPreset} onChange={(e) => setNewPreset(e.target.value)} />
          </label>
          <button className="a-btn a-btn--primary" disabled={!newName || !newPreset} onClick={() => void onCreate()}>
            Create
          </button>
        </div>
      </section>
    </>
  );
}
